import {
  Col,
  Container,
  Grid,
} from 'd2/components/Grid'
import { DateTime } from 'luxon'
import { Flexbox } from 'd2/components/Layout'
import { d } from 'd2/utils'
import { isNil } from 'lodash-es'
import { joinList } from 'd2/utils/intl'
import {
  memo,
  useEffect,
  useMemo,
} from 'react'
import { screenMdMin } from 'd2/utils/breakpoints'
import { useParams } from 'd2/hooks/useRouter'
import { useShareAlbumQuery } from './queries'
import Card from 'd2/components/Card'
import CollapsibleAlbumMetadata from 'd2/components/CollapsibleAlbumMetadata'
import DisconnectedAudioPlayer from 'd2/components/AudioPlayer/DisconnectedAudioPlayer'
import Heading2 from 'd2/components/Typography/Heading2'
import Heading3 from 'd2/components/Typography/Heading3'
import MediaQuery from 'd2/components/MediaQuery'
import Playlist from 'd2/components/AudioPlayer/Playlist'
import ShareAlbumBrandingLogo from 'd2/components/ShareAlbumBrandingLogo'
import Spacer from 'd2/components/Spacer'
import useAudioPlayer from 'd2/hooks/useAudioPlayer'
import useTranslations from './translations'
import type { Props } from './types'

const ShareAlbum = memo<Props>(() => {
  const { albumEncoded: secret } = useParams()
  const t = useTranslations()
  const [data, loadingSwitch] = useShareAlbumQuery(secret ?? '')
  const {
    currentSong,
    currentTime,
    playing,
    previousSong,
    setCurrentSong,
    setCurrentTime,
    setPlaying,
    togglePlaying,
  } = useAudioPlayer()

  useEffect(() => {
    if (isNil(currentSong) && data) {
      const [firstSong] = data.playlistData
      const { name, originalUrl, track } = d(firstSong)
      setCurrentSong({
        name,
        src: originalUrl ?? undefined,
        track,
      })
    }
  }, [currentSong, data, setCurrentSong])

  const artistNames = useMemo(() => {
    if (!data?.artistName) return t.noData
    const {
      additionalMainArtists,
      artistName,
    } = data
    const additionalMainArtistNames: string[] = additionalMainArtists.map(({ name }) => name)
    const names = [artistName, ...additionalMainArtistNames]
    return joinList(names)
  }, [data, t])

  if (!data) return loadingSwitch

  const {
    albumArtworkUrl,
    albumName,
    artistName,
    collaboratingArtists,
    copyrightName,
    copyrightYear,
    genre,
    label,
    partners,
    playlistData,
    releaseDate,
    territories,
    upc,
  } = data

  const renderedMetadata = (
    <CollapsibleAlbumMetadata
      albumName={albumName}
      artistName={artistName}
      collaboratingArtists={collaboratingArtists}
      copyrightName={copyrightName}
      copyrightYear={copyrightYear}
      genre={genre}
      label={label}
      partners={partners}
      territories={territories}
      upc={upc}
    />
  )

  const renderedAudioPlayer = (
    <DisconnectedAudioPlayer
      autoPlayOnTrackChange={Boolean(previousSong)}
      currentTime={currentTime}
      name={currentSong?.name}
      playing={playing}
      setCurrentTime={setCurrentTime}
      setPlaying={setPlaying}
      src={currentSong?.src}
      togglePlaying={togglePlaying}
    />
  )

  const renderedSubText = (<>
    <Heading3
      variant='light'
    >
      { t.byArtist(artistNames) }
    </Heading3>
    <Spacer />
    <Heading3
      variant='light'
    >
      { releaseDate
        ? t.releaseDate(DateTime.fromISO(releaseDate).toUTC()
          .toFormat('MM/dd/yyyy'))
        : t.noData }
    </Heading3>
  </>)

  return (
    <Container>
      <Grid
        justifyContent='center'
        layout
      >
        <Col md={10}>
          <Grid>
            <Col
              lg={3}
              md={3}
              sm={4}
              xs={6}
            >
              <ShareAlbumBrandingLogo />
            </Col>
            <Col sm={12}>
              <Card>
                <Grid>
                  <Col md={4}>
                    <Col md={12}>
                      <img
                        alt={t.albumArtwork}
                        src={albumArtworkUrl ?? undefined}
                      />
                    </Col>
                    <MediaQuery minWidth={screenMdMin}>
                      <Spacer />
                      { renderedAudioPlayer }
                    </MediaQuery>
                    <MediaQuery minWidth={screenMdMin}>
                      { renderedMetadata }
                    </MediaQuery>
                  </Col>
                  <Col md={8}>
                    <Heading2
                      variant='light'
                    >
                      { albumName ?? t.noData }
                    </Heading2>
                    <Spacer />
                    <MediaQuery minWidth={screenMdMin}>
                      <Flexbox align='spaceBetween'>
                        { renderedSubText }
                      </Flexbox>
                    </MediaQuery>
                    <MediaQuery maxWidth={screenMdMin}>
                      { renderedSubText }
                    </MediaQuery>
                    <Spacer />
                    <MediaQuery maxWidth={screenMdMin}>
                      { renderedAudioPlayer }
                    </MediaQuery>
                    <Spacer />
                    <Playlist
                      currentSong={currentSong}
                      playing={playing}
                      playlistData={playlistData}
                      setCurrentSong={setCurrentSong}
                      setPlaying={setPlaying}
                      togglePlaying={togglePlaying}
                    />
                    <MediaQuery maxWidth={screenMdMin}>
                      { renderedMetadata }
                    </MediaQuery>
                  </Col>
                </Grid>
              </Card>
            </Col>
          </Grid>
        </Col>
      </Grid>
    </Container>
  )
})

ShareAlbum.displayName = 'ShareAlbum'

export default ShareAlbum
